import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Package, Users, TrendingUp, MapPin, Plus, LogOut, Menu, X, 
  Settings, RefreshCw, Clock, DollarSign, Phone, Truck, Eye,
  CheckCircle, XCircle, Navigation, Send, UserPlus
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import axios from 'axios';
import { getAuthHeaders, getUser, logout } from '../utils/auth';
import CreateJobModal from '../components/CreateJobModal';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Fix for default marker icons in Leaflet with webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom driver icon
const driverIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#3b82f6" width="32" height="32">
      <circle cx="12" cy="12" r="10" fill="#3b82f6" stroke="#1e3a8a" stroke-width="2"/>
      <path d="M8 14l4-8 4 8H8z" fill="white"/>
    </svg>
  `),
  iconSize: [32, 32],
  iconAnchor: [16, 16],
  popupAnchor: [0, -16],
});

const availableDriverIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" width="32" height="32">
      <circle cx="16" cy="16" r="14" fill="#22c55e" stroke="#166534" stroke-width="2"/>
      <circle cx="16" cy="12" r="4" fill="white"/>
      <path d="M8 24c0-4 4-6 8-6s8 2 8 6" fill="white"/>
    </svg>
  `),
  iconSize: [32, 32],
  iconAnchor: [16, 16],
  popupAnchor: [0, -16],
});

const busyDriverIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" width="32" height="32">
      <circle cx="16" cy="16" r="14" fill="#f59e0b" stroke="#b45309" stroke-width="2"/>
      <circle cx="16" cy="12" r="4" fill="white"/>
      <path d="M8 24c0-4 4-6 8-6s8 2 8 6" fill="white"/>
    </svg>
  `),
  iconSize: [32, 32],
  iconAnchor: [16, 16],
  popupAnchor: [0, -16],
});

const DispatcherDashboard = () => {
  const navigate = useNavigate();
  const user = getUser();
  const [stats, setStats] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateJob, setShowCreateJob] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [currentView, setCurrentView] = useState('dashboard');
  const [selectedJob, setSelectedJob] = useState(null);
  const [showJobDetail, setShowJobDetail] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    if (!user) {
      navigate('/dispatcher/login');
      return;
    }
    loadDashboardData();
    const interval = setInterval(loadDashboardData, 10000);
    return () => clearInterval(interval);
  }, []);

  const loadDashboardData = async () => {
    try {
      const [statsRes, jobsRes, driversRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/dispatch/dashboard/stats`, { headers: getAuthHeaders() }),
        axios.get(`${BACKEND_URL}/api/dispatch/jobs`, { headers: getAuthHeaders() }),
        axios.get(`${BACKEND_URL}/api/dispatch/drivers`, { headers: getAuthHeaders() })
      ]);
      
      setStats(statsRes.data.stats);
      setJobs(jobsRes.data.jobs || []);
      setDrivers(driversRes.data.drivers || []);
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/dispatcher/login');
  };

  const offerJobToDriver = async (jobId, driverId) => {
    try {
      await axios.post(
        `${BACKEND_URL}/api/dispatch/jobs/offer`,
        { job_id: jobId, driver_id: driverId },
        { headers: getAuthHeaders() }
      );
      loadDashboardData();
      setShowJobDetail(false);
    } catch (error) {
      console.error('Error offering job:', error);
      alert(error.response?.data?.detail || 'Failed to offer job');
    }
  };

  const offerJobToMultipleDrivers = async (jobId, driverIds) => {
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/dispatch/jobs/offer-multiple`,
        { job_id: jobId, driver_ids: driverIds },
        { headers: getAuthHeaders() }
      );
      alert(response.data.message);
      loadDashboardData();
      setShowJobDetail(false);
    } catch (error) {
      console.error('Error offering job:', error);
      alert(error.response?.data?.detail || 'Failed to offer job');
    }
  };

  const pullBackOffer = async (jobId, driverId = null) => {
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/dispatch/jobs/pull-back`,
        { job_id: jobId, driver_id: driverId },
        { headers: getAuthHeaders() }
      );
      alert(response.data.message);
      loadDashboardData();
      setShowJobDetail(false);
    } catch (error) {
      console.error('Error pulling back offer:', error);
      alert(error.response?.data?.detail || 'Failed to pull back offer');
    }
  };

  const cancelJob = async (jobId) => {
    const reason = prompt('Enter cancellation reason (optional):');
    try {
      await axios.post(
        `${BACKEND_URL}/api/dispatch/jobs/cancel`,
        { job_id: jobId, reason: reason },
        { headers: getAuthHeaders() }
      );
      alert('Job cancelled successfully');
      loadDashboardData();
      setShowJobDetail(false);
    } catch (error) {
      console.error('Error cancelling job:', error);
      alert(error.response?.data?.detail || 'Failed to cancel job');
    }
  };

  const assignJobToDriver = async (jobId, driverId) => {
    try {
      await axios.post(
        `${BACKEND_URL}/api/dispatch/jobs/assign-direct`,
        { job_id: jobId, driver_id: driverId },
        { headers: getAuthHeaders() }
      );
      loadDashboardData();
      setShowJobDetail(false);
    } catch (error) {
      console.error('Error assigning job:', error);
      alert(error.response?.data?.detail || 'Failed to assign job');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      offered: 'bg-blue-100 text-blue-800 border-blue-200',
      accepted: 'bg-green-100 text-green-800 border-green-200',
      in_transit: 'bg-purple-100 text-purple-800 border-purple-200',
      delivered: 'bg-slate-100 text-slate-800 border-slate-200',
      cancelled: 'bg-red-100 text-red-800 border-red-200',
      rejected: 'bg-red-100 text-red-800 border-red-200'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const filteredJobs = filterStatus === 'all' 
    ? jobs 
    : jobs.filter(j => j.status === filterStatus);

  // Calculate map center from driver locations
  const mapCenter = drivers.length > 0 && drivers.some(d => d.current_location)
    ? [
        drivers.filter(d => d.current_location)[0]?.current_location?.lat || 45.5152,
        drivers.filter(d => d.current_location)[0]?.current_location?.lng || -122.6784
      ]
    : [47.4762, -122.2603]; // Default to Tukwila, WA

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <Package className="h-12 w-12 text-slate-400 animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 flex">
      {/* Sidebar */}
      <div className={`${sidebarOpen ? 'w-64' : 'w-20'} bg-slate-900 text-white transition-all duration-300 flex flex-col fixed h-full z-40`}>
        <div className="p-4 border-b border-slate-800 flex items-center justify-between">
          {sidebarOpen && (
            <div className="flex items-center space-x-3">
              <div className="bg-gradient-to-br from-blue-600 to-blue-400 p-2 rounded-lg">
                <Truck className="h-6 w-6 text-white" />
              </div>
              <div>
                <div className="font-bold">SABEY</div>
                <div className="text-xs text-slate-400">DISPATCH</div>
              </div>
            </div>
          )}
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2 hover:bg-slate-800 rounded-lg">
            {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        <nav className="flex-1 p-4 space-y-2">
          <button 
            onClick={() => setCurrentView('dashboard')}
            className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
              currentView === 'dashboard' ? 'bg-blue-600 text-white' : 'text-slate-300 hover:bg-slate-800'
            }`}
          >
            <TrendingUp className="h-5 w-5" />
            {sidebarOpen && <span>Dashboard</span>}
          </button>
          
          <button 
            onClick={() => setCurrentView('jobs')}
            className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
              currentView === 'jobs' ? 'bg-blue-600 text-white' : 'text-slate-300 hover:bg-slate-800'
            }`}
          >
            <Package className="h-5 w-5" />
            {sidebarOpen && <span>Jobs</span>}
          </button>
          
          <button 
            onClick={() => setCurrentView('map')}
            className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
              currentView === 'map' ? 'bg-blue-600 text-white' : 'text-slate-300 hover:bg-slate-800'
            }`}
          >
            <MapPin className="h-5 w-5" />
            {sidebarOpen && <span>Live Map</span>}
          </button>
          
          <button 
            onClick={() => setCurrentView('drivers')}
            className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
              currentView === 'drivers' ? 'bg-blue-600 text-white' : 'text-slate-300 hover:bg-slate-800'
            }`}
          >
            <Users className="h-5 w-5" />
            {sidebarOpen && <span>Drivers</span>}
          </button>
          
          <button 
            onClick={() => setShowCreateJob(true)}
            className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg bg-green-600 hover:bg-green-500 text-white transition-colors"
          >
            <Plus className="h-5 w-5" />
            {sidebarOpen && <span>Create Job</span>}
          </button>
          
          <div className="pt-4 border-t border-slate-800">
            {user?.role === 'admin' && (
              <button 
                onClick={() => navigate('/admin')}
                className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-slate-300 hover:bg-slate-800"
              >
                <UserPlus className="h-5 w-5" />
                {sidebarOpen && <span>Admin Panel</span>}
              </button>
            )}
            <button 
              onClick={() => navigate('/sftp-inbound')}
              className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-slate-300 hover:bg-slate-800"
            >
              <Package className="h-5 w-5" />
              {sidebarOpen && <span>Choice Integration</span>}
            </button>
            <button 
              onClick={() => navigate('/sftp-config')}
              className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-slate-300 hover:bg-slate-800"
            >
              <Settings className="h-5 w-5" />
              {sidebarOpen && <span>SFTP Config</span>}
            </button>
          </div>
        </nav>

        <div className="p-4 border-t border-slate-800">
          <div className={`${sidebarOpen ? '' : 'hidden'} mb-3`}>
            <div className="text-sm font-semibold">{user?.full_name}</div>
            <div className="text-xs text-slate-400 capitalize">{user?.role}</div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg hover:bg-red-600 transition-colors"
          >
            <LogOut className="h-5 w-5" />
            {sidebarOpen && <span>Logout</span>}
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className={`flex-1 ${sidebarOpen ? 'ml-64' : 'ml-20'} transition-all duration-300`}>
        <div className="p-6">
          {/* Dashboard View */}
          {currentView === 'dashboard' && (
            <>
              <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl font-bold text-slate-900">Dispatch Dashboard</h1>
                <Button onClick={loadDashboardData} variant="outline" size="sm">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh
                </Button>
              </div>

              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <Card className="p-4 bg-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm text-slate-600">Total Jobs</div>
                      <div className="text-3xl font-bold text-slate-900">{stats?.total_jobs || 0}</div>
                    </div>
                    <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center">
                      <Package className="h-6 w-6 text-slate-600" />
                    </div>
                  </div>
                </Card>
                <Card className="p-4 bg-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm text-slate-600">Active Jobs</div>
                      <div className="text-3xl font-bold text-blue-600">{stats?.active_jobs || 0}</div>
                    </div>
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                      <TrendingUp className="h-6 w-6 text-blue-600" />
                    </div>
                  </div>
                </Card>
                <Card className="p-4 bg-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm text-slate-600">Today's Deliveries</div>
                      <div className="text-3xl font-bold text-green-600">{stats?.todays_deliveries || 0}</div>
                    </div>
                    <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                      <CheckCircle className="h-6 w-6 text-green-600" />
                    </div>
                  </div>
                </Card>
                <Card className="p-4 bg-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm text-slate-600">Available Drivers</div>
                      <div className="text-3xl font-bold text-purple-600">
                        {stats?.available_drivers || 0}/{stats?.total_drivers || 0}
                      </div>
                    </div>
                    <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                      <Users className="h-6 w-6 text-purple-600" />
                    </div>
                  </div>
                </Card>
              </div>

              {/* Quick Actions & Recent Activity */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Recent Jobs */}
                <div className="lg:col-span-2">
                  <Card className="bg-white">
                    <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                      <h2 className="text-lg font-semibold text-slate-900">Recent Jobs</h2>
                      <Button onClick={() => setShowCreateJob(true)} size="sm" className="bg-slate-800">
                        <Plus className="h-4 w-4 mr-2" />
                        New Job
                      </Button>
                    </div>
                    <div className="divide-y divide-slate-100">
                      {jobs.slice(0, 5).map((job) => (
                        <div 
                          key={job.id} 
                          className="p-4 hover:bg-slate-50 cursor-pointer transition-colors"
                          onClick={() => {
                            setSelectedJob(job);
                            setShowJobDetail(true);
                          }}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="font-mono text-sm font-semibold text-slate-900">{job.job_number}</div>
                            <span className={`px-2 py-1 rounded-full text-xs font-semibold border ${getStatusColor(job.status)}`}>
                              {job.status.replace('_', ' ').toUpperCase()}
                            </span>
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <div className="text-slate-600">
                              {job.pickup_location?.city} → {job.delivery_location?.city}
                            </div>
                            <div className="font-semibold text-green-600">${job.pay_amount}</div>
                          </div>
                        </div>
                      ))}
                      {jobs.length === 0 && (
                        <div className="p-8 text-center text-slate-500">
                          <Package className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                          <p>No jobs yet. Create your first job!</p>
                        </div>
                      )}
                    </div>
                  </Card>
                </div>

                {/* Available Drivers */}
                <Card className="bg-white">
                  <div className="p-4 border-b border-slate-200">
                    <h2 className="text-lg font-semibold text-slate-900">Available Drivers</h2>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {drivers.filter(d => d.is_available).map((driver) => (
                      <div key={driver.id} className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-semibold text-slate-900">{driver.full_name}</div>
                            <div className="text-sm text-slate-600">{driver.vehicle_type}</div>
                          </div>
                          <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                        </div>
                      </div>
                    ))}
                    {drivers.filter(d => d.is_available).length === 0 && (
                      <div className="p-6 text-center text-slate-500">
                        <Users className="h-10 w-10 mx-auto mb-2 text-slate-300" />
                        <p className="text-sm">No drivers available</p>
                      </div>
                    )}
                  </div>
                </Card>
              </div>
            </>
          )}

          {/* Jobs View */}
          {currentView === 'jobs' && (
            <>
              <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl font-bold text-slate-900">All Jobs</h1>
                <div className="flex items-center space-x-3">
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="px-3 py-2 border border-slate-300 rounded-lg bg-white text-sm"
                  >
                    <option value="all">All Status</option>
                    <option value="pending">Pending</option>
                    <option value="offered">Offered</option>
                    <option value="accepted">Accepted</option>
                    <option value="in_transit">In Transit</option>
                    <option value="delivered">Delivered</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                  <Button onClick={() => setShowCreateJob(true)} className="bg-slate-800">
                    <Plus className="h-4 w-4 mr-2" />
                    Create Job
                  </Button>
                </div>
              </div>

              <Card className="bg-white overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Job #</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Tracking</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Route</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Status</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Driver</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Pay</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {filteredJobs.map((job) => {
                        const assignedDriver = drivers.find(d => d.id === job.driver_id);
                        return (
                          <tr key={job.id} className="hover:bg-slate-50">
                            <td className="py-3 px-4 font-mono text-sm font-semibold">{job.job_number}</td>
                            <td className="py-3 px-4 text-sm text-slate-600">{job.tracking_number || '-'}</td>
                            <td className="py-3 px-4 text-sm">
                              <div>{job.pickup_location?.city}, {job.pickup_location?.state}</div>
                              <div className="text-slate-400">→ {job.delivery_location?.city}, {job.delivery_location?.state}</div>
                            </td>
                            <td className="py-3 px-4">
                              <span className={`px-2 py-1 rounded-full text-xs font-semibold border ${getStatusColor(job.status)}`}>
                                {job.status.replace('_', ' ').toUpperCase()}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-sm">{assignedDriver?.full_name || '-'}</td>
                            <td className="py-3 px-4 text-sm font-semibold text-green-600">${job.pay_amount}</td>
                            <td className="py-3 px-4">
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => {
                                  setSelectedJob(job);
                                  setShowJobDetail(true);
                                }}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </Card>
            </>
          )}

          {/* Map View */}
          {currentView === 'map' && (
            <>
              <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl font-bold text-slate-900">Live Driver Map</h1>
                <Button onClick={loadDashboardData} variant="outline" size="sm">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh
                </Button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                <div className="lg:col-span-3">
                  <Card className="bg-white overflow-hidden" style={{ height: '600px' }}>
                    <MapContainer 
                      center={mapCenter} 
                      zoom={12} 
                      style={{ height: '100%', width: '100%' }}
                    >
                      <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      />
                      {drivers.map((driver) => {
                        if (!driver.current_location) return null;
                        return (
                          <Marker
                            key={driver.id}
                            position={[driver.current_location.lat, driver.current_location.lng]}
                            icon={driver.is_available ? availableDriverIcon : busyDriverIcon}
                          >
                            <Popup>
                              <div className="text-center">
                                <div className="font-bold">{driver.full_name}</div>
                                <div className="text-sm text-slate-600">{driver.vehicle_type}</div>
                                <div className={`text-xs mt-1 ${driver.is_available ? 'text-green-600' : 'text-yellow-600'}`}>
                                  {driver.is_available ? '● Available' : '● On Delivery'}
                                </div>
                                {driver.phone && (
                                  <a href={`tel:${driver.phone}`} className="text-blue-600 text-xs">
                                    {driver.phone}
                                  </a>
                                )}
                              </div>
                            </Popup>
                          </Marker>
                        );
                      })}
                    </MapContainer>
                  </Card>
                </div>

                <Card className="bg-white">
                  <div className="p-4 border-b border-slate-200">
                    <h2 className="font-semibold text-slate-900">Driver Legend</h2>
                  </div>
                  <div className="p-4 space-y-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-4 h-4 bg-green-500 rounded-full"></div>
                      <span className="text-sm">Available</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="w-4 h-4 bg-yellow-500 rounded-full"></div>
                      <span className="text-sm">On Delivery</span>
                    </div>
                  </div>
                  
                  <div className="p-4 border-t border-slate-200">
                    <h3 className="font-semibold text-slate-900 mb-3">All Drivers</h3>
                    <div className="space-y-3">
                      {drivers.map((driver) => (
                        <div key={driver.id} className="flex items-center justify-between">
                          <div>
                            <div className="text-sm font-medium">{driver.full_name}</div>
                            <div className="text-xs text-slate-500">{driver.vehicle_number}</div>
                          </div>
                          <div className={`w-2 h-2 rounded-full ${driver.is_available ? 'bg-green-500' : 'bg-yellow-500'}`}></div>
                        </div>
                      ))}
                    </div>
                  </div>
                </Card>
              </div>
            </>
          )}

          {/* Drivers View */}
          {currentView === 'drivers' && (
            <>
              <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl font-bold text-slate-900">Drivers</h1>
                {user?.role === 'admin' && (
                  <Button onClick={() => navigate('/admin')} className="bg-slate-800">
                    <UserPlus className="h-4 w-4 mr-2" />
                    Add Driver
                  </Button>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {drivers.map((driver) => (
                  <Card key={driver.id} className="p-4 bg-white">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-bold text-slate-900">{driver.full_name}</h3>
                        <p className="text-sm text-slate-600">{driver.vehicle_type} • {driver.vehicle_number}</p>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                        driver.is_available 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {driver.is_available ? 'Available' : 'Busy'}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-sm border-t border-slate-100 pt-3">
                      <div>
                        <div className="text-slate-500">Deliveries</div>
                        <div className="font-semibold">{driver.total_deliveries || 0}</div>
                      </div>
                      <div>
                        <div className="text-slate-500">Rating</div>
                        <div className="font-semibold text-yellow-600">{driver.rating || 5.0} ★</div>
                      </div>
                    </div>
                    {driver.phone && (
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="w-full mt-3"
                        onClick={() => window.location.href = `tel:${driver.phone}`}
                      >
                        <Phone className="h-4 w-4 mr-2" />
                        Call Driver
                      </Button>
                    )}
                  </Card>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Create Job Modal */}
      <CreateJobModal 
        isOpen={showCreateJob}
        onClose={() => setShowCreateJob(false)}
        onJobCreated={loadDashboardData}
        drivers={drivers}
      />

      {/* Job Detail Modal */}
      {showJobDetail && selectedJob && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl bg-white max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <div className="text-sm text-slate-500">Job Details</div>
                  <h2 className="text-2xl font-bold text-slate-900">{selectedJob.job_number}</h2>
                </div>
                <button onClick={() => setShowJobDetail(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className={`px-3 py-1 rounded-full text-sm font-semibold border ${getStatusColor(selectedJob.status)}`}>
                    {selectedJob.status.replace('_', ' ').toUpperCase()}
                  </span>
                  <span className="text-2xl font-bold text-green-600">${selectedJob.pay_amount}</span>
                </div>

                {selectedJob.tracking_number && (
                  <div className="bg-slate-50 rounded-lg p-3">
                    <div className="text-xs text-slate-500">Tracking Number</div>
                    <div className="font-mono font-semibold">{selectedJob.tracking_number}</div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-green-50 rounded-lg p-3">
                    <div className="flex items-center space-x-2 mb-2">
                      <MapPin className="h-4 w-4 text-green-600" />
                      <span className="text-sm font-semibold text-green-600">Pickup</span>
                    </div>
                    <div className="text-sm">{selectedJob.pickup_location?.address}</div>
                    <div className="text-sm text-slate-600">
                      {selectedJob.pickup_location?.city}, {selectedJob.pickup_location?.state}
                    </div>
                  </div>
                  <div className="bg-red-50 rounded-lg p-3">
                    <div className="flex items-center space-x-2 mb-2">
                      <MapPin className="h-4 w-4 text-red-600" />
                      <span className="text-sm font-semibold text-red-600">Delivery</span>
                    </div>
                    <div className="text-sm">{selectedJob.delivery_location?.address}</div>
                    <div className="text-sm text-slate-600">
                      {selectedJob.delivery_location?.city}, {selectedJob.delivery_location?.state}
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-6 text-sm text-slate-600">
                  <span><Clock className="h-4 w-4 inline mr-1" />{selectedJob.estimated_duration_minutes} min</span>
                  <span><Navigation className="h-4 w-4 inline mr-1" />{selectedJob.distance_miles} miles</span>
                </div>

                {selectedJob.special_instructions && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <div className="text-xs text-yellow-700 font-semibold mb-1">Special Instructions</div>
                    <div className="text-sm text-yellow-800">{selectedJob.special_instructions}</div>
                  </div>
                )}

                {/* Assign/Offer to Driver */}
                {selectedJob.status === 'pending' && (
                  <div className="border-t border-slate-200 pt-4">
                    <h3 className="font-semibold text-slate-900 mb-3">Assign to Driver</h3>
                    
                    {/* Offer to All Available Drivers */}
                    {drivers.filter(d => d.is_available).length > 1 && (
                      <div className="mb-4">
                        <Button 
                          className="w-full bg-blue-600 hover:bg-blue-500"
                          onClick={() => offerJobToMultipleDrivers(
                            selectedJob.id, 
                            drivers.filter(d => d.is_available).map(d => d.id)
                          )}
                        >
                          <Send className="h-4 w-4 mr-2" />
                          Offer to All Available Drivers ({drivers.filter(d => d.is_available).length})
                        </Button>
                      </div>
                    )}
                    
                    <div className="space-y-2">
                      {drivers.filter(d => d.is_available).map((driver) => (
                        <div key={driver.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div>
                            <div className="font-semibold">{driver.full_name}</div>
                            <div className="text-sm text-slate-500">{driver.vehicle_type}</div>
                          </div>
                          <div className="flex space-x-2">
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => offerJobToDriver(selectedJob.id, driver.id)}
                            >
                              <Send className="h-4 w-4 mr-1" />
                              Offer
                            </Button>
                            <Button 
                              size="sm" 
                              className="bg-slate-800"
                              onClick={() => assignJobToDriver(selectedJob.id, driver.id)}
                            >
                              Assign
                            </Button>
                          </div>
                        </div>
                      ))}
                      {drivers.filter(d => d.is_available).length === 0 && (
                        <p className="text-sm text-slate-500 text-center py-4">No available drivers</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Pull Back Offer */}
                {selectedJob.status === 'offered' && (
                  <div className="border-t border-slate-200 pt-4">
                    <h3 className="font-semibold text-slate-900 mb-3">Manage Offer</h3>
                    <div className="space-y-2">
                      <Button 
                        className="w-full bg-yellow-600 hover:bg-yellow-500"
                        onClick={() => pullBackOffer(selectedJob.id)}
                      >
                        Pull Back All Offers
                      </Button>
                      <p className="text-xs text-slate-500 text-center">
                        This will cancel all pending offers and return job to pending status
                      </p>
                    </div>
                  </div>
                )}

                {/* Cancel Job */}
                {!['delivered', 'cancelled'].includes(selectedJob.status) && (
                  <div className="border-t border-slate-200 pt-4">
                    <Button 
                      variant="outline"
                      className="w-full border-red-300 text-red-600 hover:bg-red-50"
                      onClick={() => cancelJob(selectedJob.id)}
                    >
                      Cancel This Job
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default DispatcherDashboard;
